import { SubmitDealStepProps, TOVScore } from '@/types/submit-deal.types';
import { useMemo } from 'react';

// TOV = Total Observable Value
// Score interne uniquement — jamais affiché publiquement
// Staff et admin seulement après soumission

const PILLAR_QUESTIONS = {
  financial: [
    {
      key: 'revenueStability' as keyof TOVScore,
      labelEn: 'Revenue Stability (last 3 years)',
      labelFr: 'Stabilité des revenus (3 dernières années)',
      max: 3,
      optionsEn: ['Declining', 'Flat', 'Moderate growth', 'Strong consistent growth'],
      optionsFr: ['En baisse', 'Stable', 'Croissance modérée', 'Forte croissance constante'],
    },
    {
      key: 'ebitdaTrend' as keyof TOVScore,
      labelEn: 'EBITDA Trend (last 3 years)',
      labelFr: 'Tendance EBITDA (3 dernières années)',
      max: 3,
      optionsEn: ['Declining', 'Flat', 'Improving', 'Strongly improving'],
      optionsFr: ['En baisse', 'Stable', 'En amélioration', 'Forte amélioration'],
    },
    {
      key: 'grossMarginLevel' as keyof TOVScore,
      labelEn: 'Gross Margin Level',
      labelFr: 'Niveau de marge brute',
      max: 3,
      optionsEn: ['Below sector avg', 'At sector avg', 'Above sector avg', 'Significantly above'],
      optionsFr: ['Sous la moy. sectorielle', 'Dans la moy.', 'Au-dessus', 'Nettement supérieure'],
    },
    {
      key: 'debtLevel' as keyof TOVScore,
      labelEn: 'Current Debt Level',
      labelFr: "Niveau d'endettement actuel",
      max: 3,
      optionsEn: ['High (>4x EBITDA)', 'Moderate (2-4x)', 'Low (1-2x)', 'Minimal (<1x)'],
      optionsFr: ['Élevé (>4x EBITDA)', 'Modéré (2-4x)', 'Faible (1-2x)', 'Minimal (<1x)'],
    },
    {
      key: 'cashFlowPredictability' as keyof TOVScore,
      labelEn: 'Cash Flow Predictability',
      labelFr: 'Prévisibilité du flux de trésorerie',
      max: 3,
      optionsEn: ['Very unpredictable', 'Somewhat predictable', 'Predictable', 'Highly predictable'],
      optionsFr: ['Très imprévisible', 'Assez prévisible', 'Prévisible', 'Très prévisible'],
    },
    {
      key: 'sellerFinancingOffered' as keyof TOVScore,
      labelEn: 'Seller Financing Offered',
      labelFr: 'Financement vendeur offert',
      max: 2,
      optionsEn: ['No', 'Partial (<30%)', 'Yes (≥30%)'],
      optionsFr: ['Non', 'Partiel (<30%)', 'Oui (≥30%)'],
    },
  ],
  humanCapital: [
    {
      key: 'managementTeamStrength' as keyof TOVScore,
      labelEn: 'Management Team Strength',
      labelFr: "Force de l'équipe de gestion",
      max: 5,
      optionsEn: ['No team', 'Weak team', 'Average', 'Good team', 'Strong team', 'Exceptional team'],
      optionsFr: ['Aucune équipe', 'Équipe faible', 'Moyenne', 'Bonne équipe', 'Équipe forte', 'Équipe exceptionnelle'],
    },
    {
      key: 'employeeRetention' as keyof TOVScore,
      labelEn: 'Employee Retention Rate',
      labelFr: 'Taux de rétention des employés',
      max: 3,
      optionsEn: ['High turnover (>30%/yr)', 'Moderate (15-30%)', 'Good (<15%)', 'Excellent (<5%)'],
      optionsFr: ['Fort roulement (>30%/an)', 'Modéré (15-30%)', 'Bon (<15%)', 'Excellent (<5%)'],
    },
    {
      key: 'ownerDependency' as keyof TOVScore,
      labelEn: 'Owner Dependency (lower = better)',
      labelFr: 'Dépendance au propriétaire (moins = mieux)',
      max: 3,
      optionsEn: ['Fully dependent', 'Highly dependent', 'Partially independent', 'Fully autonomous'],
      optionsFr: ['Totalement dépendant', 'Très dépendant', 'Partiellement autonome', 'Totalement autonome'],
    },
    {
      key: 'keyPersonRisk' as keyof TOVScore,
      labelEn: 'Key Person Risk',
      labelFr: 'Risque personne clé',
      max: 3,
      optionsEn: ['Critical dependency', 'High risk', 'Moderate risk', 'Low risk'],
      optionsFr: ['Dépendance critique', 'Risque élevé', 'Risque modéré', 'Risque faible'],
    },
    {
      key: 'hrDocumentation' as keyof TOVScore,
      labelEn: 'HR Documentation & Policies',
      labelFr: 'Documentation RH et politiques',
      max: 2,
      optionsEn: ['None', 'Partial', 'Complete'],
      optionsFr: ['Aucune', 'Partielle', 'Complète'],
    },
  ],
  market: [
    {
      key: 'customerDiversification' as keyof TOVScore,
      labelEn: 'Customer Diversification',
      labelFr: 'Diversification de la clientèle',
      max: 5,
      optionsEn: ['1 client (100%)', 'Very concentrated', 'Concentrated', 'Diversified', 'Well diversified', 'Highly diversified'],
      optionsFr: ['1 client (100%)', 'Très concentré', 'Concentré', 'Diversifié', 'Bien diversifié', 'Très diversifié'],
    },
    {
      key: 'marketPosition' as keyof TOVScore,
      labelEn: 'Market Position',
      labelFr: 'Position sur le marché',
      max: 3,
      optionsEn: ['No differentiation', 'Some differentiation', 'Clear positioning', 'Market leader'],
      optionsFr: ['Aucune différenciation', 'Quelque différenciation', 'Positionnement clair', 'Leader du marché'],
    },
    {
      key: 'contractBacklog' as keyof TOVScore,
      labelEn: 'Contract Backlog / Recurring Revenue',
      labelFr: 'Carnet de commandes / Revenus récurrents',
      max: 3,
      optionsEn: ['None', '<3 months', '3-12 months', '>12 months'],
      optionsFr: ['Aucun', '<3 mois', '3-12 mois', '>12 mois'],
    },
    {
      key: 'competitiveAdvantage' as keyof TOVScore,
      labelEn: 'Competitive Advantage',
      labelFr: 'Avantage concurrentiel',
      max: 3,
      optionsEn: ['None identified', 'Marginal', 'Moderate moat', 'Strong moat'],
      optionsFr: ['Aucun identifié', 'Marginal', 'Avantage modéré', 'Avantage fort'],
    },
    {
      key: 'marketGrowthTrend' as keyof TOVScore,
      labelEn: 'Market Growth Trend',
      labelFr: 'Tendance de croissance du marché',
      max: 2,
      optionsEn: ['Declining market', 'Stable market', 'Growing market'],
      optionsFr: ['Marché en déclin', 'Marché stable', 'Marché en croissance'],
    },
  ],
  operations: [
    {
      key: 'processDocumentation' as keyof TOVScore,
      labelEn: 'Process Documentation',
      labelFr: 'Documentation des processus',
      max: 3,
      optionsEn: ['No documentation', 'Partial', 'Good', 'Complete SOPs'],
      optionsFr: ['Aucune documentation', 'Partielle', 'Bonne', 'Procédures complètes'],
    },
    {
      key: 'technologyModernity' as keyof TOVScore,
      labelEn: 'Technology & Systems Modernity',
      labelFr: 'Modernité des technologies et systèmes',
      max: 3,
      optionsEn: ['Outdated systems', 'Mixed', 'Modern', 'Best-in-class'],
      optionsFr: ['Systèmes obsolètes', 'Mixte', 'Moderne', "État de l'art"],
    },
    {
      key: 'legalCompliance' as keyof TOVScore,
      labelEn: 'Legal & Regulatory Compliance',
      labelFr: 'Conformité légale et réglementaire',
      max: 3,
      optionsEn: ['Issues identified', 'Minor concerns', 'Mostly compliant', 'Fully compliant'],
      optionsFr: ['Problèmes identifiés', 'Préoccupations mineures', 'Majoritairement conforme', 'Pleinement conforme'],
    },
    {
      key: 'realEstateStability' as keyof TOVScore,
      labelEn: 'Real Estate / Lease Stability',
      labelFr: 'Stabilité immobilière / Bail',
      max: 2,
      optionsEn: ['Lease <1yr or at risk', 'Lease 1-3yr', 'Lease >3yr or owned'],
      optionsFr: ['Bail <1an ou à risque', 'Bail 1-3 ans', 'Bail >3 ans ou propriété'],
    },
    {
      key: 'certifications' as keyof TOVScore,
      labelEn: 'Certifications & Licenses',
      labelFr: 'Certifications et licences',
      max: 2,
      optionsEn: ['None', 'Basic', 'Industry-specific certifications'],
      optionsFr: ['Aucune', 'Basiques', 'Certifications sectorielles'],
    },
  ],
};

function calculateScores(tov: TOVScore): {
  financial: number;
  humanCapital: number;
  market: number;
  operations: number;
  total: number;
} {
  const financial =
    (tov.revenueStability || 0) +
    (tov.ebitdaTrend || 0) +
    (tov.grossMarginLevel || 0) +
    (tov.debtLevel || 0) +
    (tov.cashFlowPredictability || 0) +
    (tov.sellerFinancingOffered || 0);

  const humanCapital =
    (tov.managementTeamStrength || 0) +
    (tov.employeeRetention || 0) +
    (tov.ownerDependency || 0) +
    (tov.keyPersonRisk || 0) +
    (tov.hrDocumentation || 0);

  const market =
    (tov.customerDiversification || 0) +
    (tov.marketPosition || 0) +
    (tov.contractBacklog || 0) +
    (tov.competitiveAdvantage || 0) +
    (tov.marketGrowthTrend || 0);

  const operations =
    (tov.processDocumentation || 0) +
    (tov.technologyModernity || 0) +
    (tov.legalCompliance || 0) +
    (tov.realEstateStability || 0) +
    (tov.certifications || 0);

  return {
    financial,
    humanCapital,
    market,
    operations,
    total: financial + humanCapital + market + operations,
  };
}

export function StepTOVScore({ formData, updateFormData, t }: SubmitDealStepProps) {
  const language = (t as any)._lang || 'en';
  const tov = formData.tovScore;

  const scores = useMemo(() => calculateScores(tov), [tov]);

  const handleChange = (key: keyof TOVScore, value: number) => {
    const updated = { ...tov, [key]: value };
    const newScores = calculateScores(updated as TOVScore);
    updateFormData('tovScore', {
      ...updated,
      financialScore: newScores.financial,
      humanCapitalScore: newScores.humanCapital,
      marketScore: newScores.market,
      operationsScore: newScores.operations,
      totalScore: newScores.total,
    });
  };

  const pillars = [
    {
      key: 'financial',
      labelEn: 'Financial Health',
      labelFr: 'Santé Financière',
      score: scores.financial,
      max: 17,
      color: 'bg-blue-500',
      questions: PILLAR_QUESTIONS.financial,
    },
    {
      key: 'humanCapital',
      labelEn: 'Human Capital',
      labelFr: 'Capital Humain',
      score: scores.humanCapital,
      max: 17,
      color: 'bg-green-500',
      questions: PILLAR_QUESTIONS.humanCapital,
    },
    {
      key: 'market',
      labelEn: 'Market & Customers',
      labelFr: 'Marché & Clients',
      score: scores.market,
      max: 16,
      color: 'bg-purple-500',
      questions: PILLAR_QUESTIONS.market,
    },
    {
      key: 'operations',
      labelEn: 'Operations & Governance',
      labelFr: 'Opérations & Gouvernance',
      score: scores.operations,
      max: 13,
      color: 'bg-orange-500',
      questions: PILLAR_QUESTIONS.operations,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-900">
          {language === 'fr'
            ? "🔒 Ces informations sont utilisées en interne par AcquizGroup pour évaluer l'attractivité de votre entreprise. Elles ne sont jamais affichées publiquement."
            : '🔒 This information is used internally by AcquizGroup to assess your business attractiveness. It is never displayed publicly.'}
        </p>
      </div>

      {/* Total score display */}
      <div className="bg-gradient-to-br from-[#1D314D] to-[#2c4a73] text-white rounded-xl p-6 text-center">
        <p className="text-sm uppercase tracking-wider opacity-80 mb-2">
          {language === 'fr' ? "Score d'Attractivité Total" : 'Total Attractiveness Score'}
        </p>
        <p className="text-5xl font-bold mb-4">{scores.total}/63</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="opacity-80">{language === 'fr' ? 'Financier' : 'Financial'}: </span>
            <span className="font-semibold">{scores.financial}/17</span>
          </div>
          <div>
            <span className="opacity-80">{language === 'fr' ? 'Humain' : 'Human'}: </span>
            <span className="font-semibold">{scores.humanCapital}/17</span>
          </div>
          <div>
            <span className="opacity-80">{language === 'fr' ? 'Marché' : 'Market'}: </span>
            <span className="font-semibold">{scores.market}/16</span>
          </div>
          <div>
            <span className="opacity-80">{language === 'fr' ? 'Opérations' : 'Operations'}: </span>
            <span className="font-semibold">{scores.operations}/13</span>
          </div>
        </div>
      </div>

      {/* Pillars */}
      {pillars.map((pillar) => (
        <div key={pillar.key} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-[#1D314D] flex items-center gap-2">
              <span className={`inline-block w-3 h-3 rounded-full ${pillar.color}`} />
              {language === 'fr' ? pillar.labelFr : pillar.labelEn}
            </h3>
            <span className="text-sm font-bold text-[#1D314D] bg-gray-100 px-3 py-1 rounded-full">
              {pillar.score}/{pillar.max}
            </span>
          </div>

          <div className="space-y-5">
            {pillar.questions.map((q) => {
              const currentVal = (tov[q.key] as number) || 0;
              const options = language === 'fr' ? q.optionsFr : q.optionsEn;
              return (
                <div key={q.key as string}>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {language === 'fr' ? q.labelFr : q.labelEn}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {options.map((opt, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleChange(q.key, i)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          currentVal === i
                            ? 'bg-[#1D314D] text-white border-[#1D314D]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#CEAB65]'
                        }`}
                      >
                        {opt}
                        {i > 0 && (
                          <span className="ml-1 opacity-70">+{i}pt</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
